
import React from 'react';
import { motion } from 'framer-motion';
import { useApp } from '../context/AppContext';
import { Navigate, Link } from 'react-router-dom';
import { 
  ShieldAlert, CheckCircle, Trash2, Edit3, 
  Clock, ArrowLeft, ExternalLink, Inbox
} from 'lucide-react';

const Moderation: React.FC = () => {
  const { currentUser, posts, approvePost, deletePost, hasPermission } = useApp();

  if (!currentUser || !hasPermission('CAN_APPROVE_POSTS')) {
    return <Navigate to="/blog" replace />;
  }

  const pendingPosts = posts.filter(p => p.status === 'pending');

  return (
    <div className="pt-40 pb-32 min-h-screen bg-slate-50 text-slate-900">
      <div className="max-w-7xl mx-auto px-6">
        <header className="mb-12">
          <Link to="/profile" className="inline-flex items-center gap-2 text-slate-500 hover:text-indigo-600 font-bold uppercase tracking-widest text-[10px] mb-8 transition-colors">
            <ArrowLeft size={14} /> Back to Dashboard
          </Link>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h1 className="text-4xl md:text-6xl font-display font-black text-slate-900 tracking-tighter mb-4">
                Moderation <span className="text-indigo-600">Command Center</span>
              </h1>
              <p className="text-slate-500 text-lg max-w-2xl">
                Review and authorize pending intelligence submissions. Only approved content goes live for institutional dissemination.
              </p>
            </div>
            <div className="px-8 py-4 bg-white border border-slate-200 rounded-[2rem] flex items-center gap-4 shadow-sm">
              <div className="w-12 h-12 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-600">
                <Inbox size={24} />
              </div>
              <div>
                <span className="text-slate-900 font-black text-2xl">{pendingPosts.length}</span>
                <p className="text-slate-400 text-[9px] font-black uppercase tracking-widest">Pending Audit</p>
              </div>
            </div>
          </div>
        </header>

        <section className="space-y-6">
          {pendingPosts.length > 0 ? (
            pendingPosts.map((post) => (
              <motion.div 
                key={post.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-8 bg-white border border-slate-200 rounded-[3rem] flex flex-col lg:flex-row gap-10 items-center group hover:border-indigo-200 transition-all shadow-xl overflow-hidden relative"
              >
                <div className="absolute top-0 right-0 p-8 text-indigo-50 group-hover:text-indigo-100 transition-colors">
                  <ShieldAlert size={140} />
                </div>
                
                <div className="w-full lg:w-64 aspect-video rounded-2xl overflow-hidden bg-slate-100 border border-slate-200 shrink-0 relative z-10">
                  <img src={post.imageUrl} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" alt="Post preview" />
                </div>

                <div className="flex-grow relative z-10">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="px-3 py-1 bg-indigo-50 text-indigo-600 text-[9px] font-black uppercase tracking-[0.2em] rounded-lg border border-indigo-100">
                      {post.category}
                    </span>
                    <span className="text-[10px] text-slate-400 font-bold uppercase flex items-center gap-1.5">
                      <Clock size={12} /> Received {post.date}
                    </span>
                  </div>
                  
                  <h3 className="text-2xl font-display font-bold text-slate-900 mb-2 leading-tight">{post.title}</h3>
                  <div className="flex items-center gap-2 mb-6 text-slate-500 text-sm">
                    By <span className="text-indigo-600 font-bold">{post.author}</span>
                  </div>

                  <div className="flex flex-wrap gap-4">
                    <button 
                      onClick={() => approvePost(post.id)}
                      className="px-8 py-3.5 bg-green-50 text-green-600 font-black text-[10px] uppercase tracking-[0.2em] rounded-xl shadow-sm hover:bg-green-100 transition-all flex items-center gap-2 border border-green-200"
                    >
                      <CheckCircle size={14} /> Authorize Deployment
                    </button>
                    <Link 
                      to={`/edit-post/${post.id}`}
                      className="px-8 py-3.5 bg-white hover:bg-slate-50 text-slate-600 font-black text-[10px] uppercase tracking-[0.2em] rounded-xl border border-slate-200 transition-all flex items-center gap-2"
                    >
                      <Edit3 size={14} /> Audit Content
                    </Link>
                    <button 
                      onClick={() => { if(confirm('Purge this record permanently?')) deletePost(post.id); }}
                      className="px-8 py-3.5 bg-red-50 hover:bg-red-100 text-red-600 font-black text-[10px] uppercase tracking-[0.2em] rounded-xl border border-red-200 transition-all flex items-center gap-2"
                    >
                      <Trash2 size={14} /> Purge Record
                    </button>
                    <Link 
                      to={`/blog/${post.id}`}
                      target="_blank"
                      className="p-3.5 bg-white border border-slate-200 rounded-xl text-slate-400 hover:text-indigo-600 transition-all"
                      title="Preview Insight"
                    >
                      <ExternalLink size={18} />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="py-32 text-center bg-white border border-dashed border-slate-200 rounded-[4rem] shadow-sm"
            >
              <CheckCircle className="mx-auto text-green-500/40 mb-6" size={80} />
              <h3 className="text-2xl font-display font-bold text-slate-900 mb-2">Audit Queue Clear</h3>
              <p className="text-slate-400 uppercase font-black text-[10px] tracking-widest">All intelligence nodes are currently synchronized.</p>
            </motion.div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Moderation;
